import { storeToRefs } from 'pinia'
import { useDocumentsStore } from '~/stores/documents'
import type { ViewState } from '~~/shared/web-chat/viewState'
import { deriveIndexState, type IndexState } from '~~/shared/indexing/indexState'

// Builds the ViewState the chat request reports each turn — the same typed contract that
// gates which client tools the agent is offered (shared/web-chat/) and that is rendered
// into the agent's message thread.
//
// Note: `fileExplorerOpen` reads the file explorers registered with the documents store (MAR-989),
// not the panel layout. `openDocumentId` and `openSheetId` are derived from the cleanly-mapped /app/docs/[id] and /app/sheets/[id] routes (each page is
// a single-resource route the component already owns). The sheet view consumes the filter/select
// command bridge — see usePanelLayout's sheet command +.
// Max file-explorer rows reported to the agent per turn. Selection-biased: selected
// items are always included first, then current sort order fills the remainder. Keeps
// the per-turn token cost bounded while the explorer is open.
const VISIBLE_FILES_CAP = 25

/**
 * Report a row's indexing state to the agent only when it is NOT ready — the agent needs to know a
 * document has no readable text yet, and saying so for every ready row would be noise.
 *
 * Reads `status` because that is what the list payload carries; `index_state` (migration 0071) is
 * the same information at the source. It is `deriveIndexState` with `ready` withheld — never a local
 * mapping (MAR-948; `itemPendingGuard.test.ts`).
 *
 * `pendingRead` is the row's `pending_read` ("Lectura pendiente"). It was not passed until MAR-990,
 * so a document waiting on a deep read reached the agent as a ready one with no text, and the agent
 * called it out of scope and told the user to upload it again.
 */
export function indexStateForAgent(
  status: string | null | undefined,
  pendingRead?: boolean | null,
): Exclude<IndexState, 'ready'> | null {
  const state = deriveIndexState(null, status ?? null, pendingRead === true)
  return state === 'ready' ? null : state
}

export function useWebChatView() {
  const documents = useDocumentsStore()
  const { selectedIds } = storeToRefs(documents)
  const route = useRoute()

  function currentViewState(): ViewState {
    const v: ViewState = {}
    /**
     * "Is a file explorer on screen" is whether one is REGISTERED (MAR-989), not whether the sidebar
     * drawer is open. It used to read `docsDrawerOpen`, and `/app/docs` — the full-page explorer —
     * closes that drawer on mount, so the page that is nothing but a file explorer reported none.
     * The agent's rules key on this line ("The file explorer is open"), and `filter_files` /
     * `select_files` already answered from the registration, so the two disagreed: the filter moved
     * the page's list and the agent said it could not see an explorer (CHAT-2.01, 2026-10-01).
     *
     * The rows, the filter and the folder come from the surface itself, because the surface is the
     * only thing that knows what it renders. MAR-494's lesson stands: in a grouped mode the sidebar
     * renders its own filtered groups, not `items`, and the agent picks a `document_id` by matching
     * against this list — so it must be the list on screen.
     */
    const explorer = documents.fileExplorer
    if (explorer) {
      v.fileExplorerOpen = true

      // Real rows only — drop the optimistic `upload-…` placeholders.
      const rows = explorer.rows().filter(i => !i.id.startsWith('upload-'))
      const selected = selectedIds.value

      // Selection-biased cap: selected first (in list order), then the rest.
      const ordered = [
        ...rows.filter(i => selected.has(i.id)),
        ...rows.filter(i => !selected.has(i.id)),
      ].slice(0, VISIBLE_FILES_CAP)

      if (ordered.length) {
        v.visibleFiles = ordered.map(i => {
          // Only when it is NOT ready: a document mid-indexing has no text for the agent to read,
          // and one that failed never will. Ready rows say nothing (§7).
          const indexState = indexStateForAgent(i.status, i.pending_read)
          return {
            id: i.id,
            name: i.name,
            type: i.type,
            contractType: i.contract_type,
            ...(indexState ? { indexState } : {}),
          }
        })
      }
      const selectedVisible = ordered.filter(i => selected.has(i.id)).map(i => i.id)
      if (selectedVisible.length) v.selectedFileIds = selectedVisible
      // A folder is reported only while one is being listed: in a grouped mode the surface is not
      // showing a folder, and naming one invites the agent to reason about a location the user left.
      const folder = explorer.folderPath()
      if (folder) v.currentPath = folder
      const filter = explorer.filter()
      if (filter) v.fileFilter = filter
    }
    const docMatch = route.path.match(/^\/app\/docs\/(.+)$/)
    if (docMatch) v.openDocumentId = docMatch[1]
    const sheetMatch = route.path.match(/^\/app\/sheets\/(.+)$/)
    if (sheetMatch) v.openSheetId = sheetMatch[1]
    return v
  }

  return { currentViewState }
}
